import React, { memo, useCallback, useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import ProgressBar from "@hl/base-components/lib/progress/SimpleProgressBar";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";

import { BlockchainEntity } from "components/BlockchainEntityTitle/types";
import {
  generateNextCollectionRevision,
  isSeriesLikeCollection,
} from "~/features/collections/displayUtils";
import { useDuplicateCollectionMutation } from "~/features/collections/sections/ContractCollectionDashboard/menu/menu.graphql.generated";
import { GetMergedCollectionByUserPaginatedDocument } from "~/features/tokens/queries/collections.graphql.generated";

interface CollectionDuplicateFormValues {
  collectionName: string;
}

const CollectionDuplicateModal = ({
  entity,
  isOpened,
  onClose,
  onFinished,
}: {
  entity: BlockchainEntity;
  isOpened: boolean;
  onClose: () => void;
  onFinished?: () => void;
}) => {
  const [error, setError] = useState("");
  const duplicateCollectionName = generateNextCollectionRevision(
    entity.details.name
  );
  const form = useForm<CollectionDuplicateFormValues>({
    initialValues: {
      collectionName: duplicateCollectionName,
    },
    validate: (values) => ({
      collectionName:
        values.collectionName.length === 0
          ? "Duplicate collection must be named to proceed"
          : values.collectionName.length < 3
          ? "Too short name"
          : values.collectionName.length > 80
          ? "Too long name"
          : null,
    }),
  });

  const [showProgressBar, setShowProgressBar] = useState(false);
  const [originalCollectionName] = useState(entity.details.name);
  const [duplicateCollection, { loading }] = useDuplicateCollectionMutation();

  const handleOnSubmit = useCallback(
    form.onSubmit(async () => {
      try {
        setError("");
        setShowProgressBar(true);

        const duplicateCollectionResp = await duplicateCollection({
          variables: {
            collectionId: entity.id,
            data: {
              newName: form.values.collectionName,
              duplicateZip: isSeriesLikeCollection(entity.collectionType),
            },
          },
          refetchQueries: [GetMergedCollectionByUserPaginatedDocument],
        });

        if ((duplicateCollectionResp.errors?.length || 0) > 0) {
          setError(
            "There was an error with the duplication process. Please try again in a few minutes."
          );
          await logError(
            { e: duplicateCollectionResp.errors },
            `Failed to duplicate a collectionId. collectionId: ${entity.id}`
          );
        } else {
          onClose();
          onFinished?.();
        }
      } catch (error) {
        setError(
          "There was an error with the duplication process. Please try again in a few minutes."
        );
        await logError({ error, message: "duplicate collection error" });
      }
    }),
    [form, entity, onClose]
  );

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  return (
    <Modal
      opened={isOpened}
      onClose={onClose}
      size="lg"
      title="Duplicate collection"
    >
      {!showProgressBar ? (
        <form onSubmit={handleOnSubmit}>
          <Stack spacing={24}>
            <Text component="span" size="sm">
              A new draft copy of this collection with identical inputs and data
              will be created. You can edit the copy&apos;s details while
              keeping the original collection unchanged.
            </Text>
            <TextInput
              maxLength={80}
              minLength={3}
              label="Name of duplicate collection"
              {...form.getInputProps("collectionName")}
              onBlur={() => form.validateField("collectionName")}
            />
          </Stack>
          <ModalSection>
            <Group spacing={16} grow>
              <Button size="lg" color={SECONDARY_COLOR} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="lg"
                loading={loading}
                type="submit"
                disabled={!form.isValid()}
              >
                Duplicate
              </Button>
            </Group>
            {error && (
              <Text align="center" mt="xs" size="xs" color={STATUS_COLOR.ERROR}>
                {error}
              </Text>
            )}
          </ModalSection>
        </form>
      ) : (
        <>
          <ProgressBar
            step={`Copying collection ${originalCollectionName}`}
            estimatedStartTimestamp={dayjs().toDate()}
            estimatedEndTimestamp={dayjs().add(10, "seconds").toDate()}
            error={error}
          ></ProgressBar>
        </>
      )}
    </Modal>
  );
};

export default memo(CollectionDuplicateModal);
